import { contestants } from '@/mocks/contestants';
import { works } from '@/mocks/works';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Reveal from '@/components/base/Reveal';
import JsonLd from '@/components/base/JsonLd';
import { useSeo, buildAbsoluteUrl } from '@/hooks/useSeo';

const title = '选手盘点 - 黑料大赛参赛选手热度榜 | 黑料大赛';
const description =
  '黑料大赛选手盘点页，收录本期活跃选手的昵称、阵营、风格标签与近三十天热度，并列出各自代表作品，方便快速找到合口味的内容。';
const keywords = '黑料大赛选手盘点,黑料大赛选手,参赛选手,选手热度榜';

export default function Contestants() {
  useSeo({ title, description, keywords, path: '/contestants' });

  const pageUrl = buildAbsoluteUrl('/contestants');
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        name: title,
        url: pageUrl,
        description,
        inLanguage: 'zh-CN',
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: buildAbsoluteUrl('/') },
          { '@type': 'ListItem', position: 2, name: '选手盘点', item: pageUrl },
        ],
      },
      {
        '@type': 'ItemList',
        name: '黑料大赛本期选手盘点',
        numberOfItems: contestants.length,
        itemListElement: contestants.map((person, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: person.alias,
        })),
      },
    ],
  };

  return (
    <>
      <JsonLd data={schema} />
      <div className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '选手盘点' }]} />
          <h1 className="mt-4 font-heading text-3xl font-black text-foreground-950 md:text-4xl">本期选手盘点</h1>
          <p className="mt-4 max-w-2xl font-body text-sm leading-relaxed text-foreground-600 md:text-base">
            这里按近三十天的作品表现整理活跃选手。热度会随新作品上线而变动，用来看谁最近更新得勤快最合适，
            不代表作品质量高低，也别太当真。
          </p>
        </div>
      </div>

      <div className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
        <ol className="flex flex-col gap-5">
          {contestants.map((person, index) => {
            const personWork = works.find((w) => w.contestant === person.alias);
            return (
              <Reveal key={person.id} as="li" delay={index * 50}>
                <article className="grid grid-cols-1 gap-5 rounded-lg border border-background-200 bg-background-100 p-5 md:grid-cols-12 md:items-center md:p-6">
                  <div className="flex items-center gap-4 md:col-span-4">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-500 font-heading text-sm font-bold text-background-50">
                      {index + 1}
                    </span>
                    <div className="h-16 w-16 shrink-0 overflow-hidden rounded-full border border-background-300">
                      <img
                        src={person.avatar}
                        alt={`${person.alias} - 黑料大赛选手`}
                        title={`${person.alias} - 黑料大赛选手盘点`}
                        className="h-full w-full object-top"
                      />
                    </div>
                    <div className="min-w-0">
                      <h2 className="font-heading text-lg font-bold text-foreground-950">{person.alias}</h2>
                      <p className="mt-0.5 font-label text-xs text-foreground-500">
                        {person.camp} · {person.signature}
                      </p>
                    </div>
                  </div>

                  <div className="md:col-span-5">
                    <p className="font-body text-sm leading-relaxed text-foreground-700">{person.intro}</p>
                    {personWork && (
                      <a
                        href={`${__BASE_PATH__.replace(/\/+$/, '')}/works/${personWork.id}`}
                        className="mt-3 inline-flex items-center gap-1.5 whitespace-nowrap font-label text-xs text-primary-600 transition-colors hover:text-primary-700"
                      >
                        <i className="ri-file-text-line"></i>
                        代表作品：{person.bestWork}
                      </a>
                    )}
                  </div>

                  <div className="md:col-span-3">
                    <div className="flex items-center justify-between font-label text-xs text-foreground-500">
                      <span>近三十天热度</span>
                      <span className="font-heading text-base font-bold text-primary-600">{person.heat}</span>
                    </div>
                    <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-background-200">
                      <div className="h-full rounded-full bg-accent-500" style={{ width: `${person.heat}%` }}></div>
                    </div>
                    <p className="mt-3 font-label text-xs text-foreground-500">公开作品 {person.worksCount} 篇</p>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </ol>
      </div>
    </>
  );
}